/**
 * Formatting utilities for phone numbers, dates, and text transformations
 */

/**
 * Formats a phone number to Brazilian format
 * Supports both mobile (11 digits) and landline (10 digits)
 * @param value - Raw phone number string
 * @returns Formatted phone number: (00) 00000-0000 or (00) 0000-0000
 */
export const formatPhoneBrazilian = (value: string): string => {
    if (!value) return '';

    // Remove all non-numeric characters
    const numbers = value.replace(/\D/g, '');

    // Limit to 11 digits (Brazilian mobile phone max)
    const limited = numbers.slice(0, 11);

    // Apply mask based on length
    if (limited.length <= 2) {
        return limited;
    } else if (limited.length <= 6) {
        return `(${limited.slice(0, 2)}) ${limited.slice(2)}`;
    } else if (limited.length <= 10) {
        // Landline format: (00) 0000-0000
        return `(${limited.slice(0, 2)}) ${limited.slice(2, 6)}-${limited.slice(6)}`;
    } else {
        // Mobile format: (00) 00000-0000
        return `(${limited.slice(0, 2)}) ${limited.slice(2, 7)}-${limited.slice(7, 11)}`;
    }
};

/**
 * Removes phone mask and returns only numbers
 * @param value - Formatted phone number
 * @returns Raw phone number with only digits
 */
export const unformatPhone = (value: string): string => {
    return value.replace(/\D/g, '');
};

/**
 * Formats a date from ISO format (yyyy-MM-dd) to Brazilian format (dd/MM/yyyy)
 * @param date - Date string in ISO format
 * @returns Date string in Brazilian format
 */
export const formatDateBrazilian = (date: string): string => {
    if (!date) return '';

    // Check if already in Brazilian format
    if (date.includes('/')) return date;

    // Convert from yyyy-MM-dd to dd/MM/yyyy
    const parts = date.split('-');
    if (parts.length === 3) {
        return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }

    return date;
};

/**
 * Parses a date from Brazilian format (dd/MM/yyyy) to ISO format (yyyy-MM-dd)
 * @param date - Date string in Brazilian format
 * @returns Date string in ISO format
 */
export const parseDateToISO = (date: string): string => {
    if (!date) return '';

    // Check if already in ISO format
    if (date.includes('-')) return date;

    // Convert from dd/MM/yyyy to yyyy-MM-dd
    const parts = date.split('/');
    if (parts.length === 3) {
        return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }

    return date;
};

/**
 * Formats a date input with Brazilian mask (dd/MM/yyyy)
 * @param value - Raw date input
 * @returns Formatted date with mask
 */
export const formatDateInput = (value: string): string => {
    if (!value) return '';

    // Remove all non-numeric characters
    const numbers = value.replace(/\D/g, '');

    // Limit to 8 digits
    const limited = numbers.slice(0, 8);

    // Apply mask based on length
    if (limited.length <= 2) {
        return limited;
    } else if (limited.length <= 4) {
        return `${limited.slice(0, 2)}/${limited.slice(2)}`;
    } else {
        return `${limited.slice(0, 2)}/${limited.slice(2, 4)}/${limited.slice(4)}`;
    }
};

/**
 * Converts text to uppercase
 * @param value - Input text
 * @returns Uppercase text
 */
export const toUpperCase = (value: string): string => {
    return value ? value.toUpperCase() : '';
};

/**
 * Converts text to lowercase
 * @param value - Input text
 * @returns Lowercase text
 */
export const toLowerCase = (value: string): string => {
    return value ? value.toLowerCase() : '';
};

/**
 * Formats a duration from HH:MM:SS format to a user-friendly format (e.g. 1h 30min or 45min)
 * @param duration - Duration string in HH:MM:SS format
 * @returns User-friendly duration string
 */
export const formatDuration = (duration: string): string => {
    if (!duration) return '';
    const parts = duration.split(':');
    if (parts.length < 2) return duration;

    const hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);

    let result = '';
    if (hours > 0) {
        result += `${hours}h `;
    }
    if (minutes > 0 || hours === 0) {
        result += `${minutes}min`;
    }
    return result.trim();
};

/**
 * Formats a Date object to ISO date string (yyyy-MM-dd) using LOCAL timezone.
 * This avoids the timezone shift problem caused by Date.toISOString() which converts to UTC.
 * 
 * Example of the problem this solves:
 * - Local time: 2026-02-23T00:00:00 (Brazil UTC-3)
 * - toISOString(): "2026-02-22T03:00:00.000Z" (wrong day!)
 * - formatLocalDate(): "2026-02-23" (correct!)
 * 
 * @param date - Date object to format
 * @returns Date string in yyyy-MM-dd format using local timezone
 */
export const formatLocalDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};
